import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { PATHS } from "@/constants/paths";
import { useAuth } from "@/hooks/use-auth";
import { getToken } from "@/lib/auth";
import { getDocsReturnPath, redirectToDocs } from "@/lib/docs-session";

interface INonProtectedRouteProps {
  children: React.ReactNode;
}

export const NonProtectedRoute = ({ children }: INonProtectedRouteProps) => {
  const { user } = useAuth();
  const location = useLocation();
  const token = getToken();
  // NOTE: An already-authenticated user can still land here via
  // /auth/sign-in?returnUrl=/docs (e.g. the account menu's "Docs" link) —
  // that gate is the separate docs_session cookie, not this app session, so
  // they must go through the docs handoff instead of being bounced to /chat
  // like every other auth page. NSAI-6750: a deeper /docs/... returnUrl (the
  // global search docs action) lands on that page. NSAI-4426: a fresh
  // sign-in with a docs returnUrl (the proxy's 401 bounce) also ends here,
  // once the user loads, and takes the same handoff.
  const docsReturnPath = getDocsReturnPath(location.search);
  const isDocsHandoff = Boolean(user && token && docsReturnPath);

  useEffect(() => {
    // NOTE: isDocsHandoff already guarantees token and docsReturnPath are
    // truthy at runtime, but it's a plain boolean — TypeScript can't narrow
    // them from it, so the check stays here for type-safety, not redundancy.
    if (isDocsHandoff && token && docsReturnPath) {
      void redirectToDocs({ accessToken: token, returnPath: docsReturnPath });
    }
  }, [docsReturnPath, isDocsHandoff, token]);

  if (user) {
    if (isDocsHandoff) return null;
    return <Navigate to={PATHS.CHAT} replace />;
  }

  return <>{children}</>;
};
